import type { AdminVehicleGenerationDto } from '@carcheck/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useRef, useState } from 'react';
import { useForm, type UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { CatalogWriteError } from './catalogErrors';
import {
  EMPTY_GENERATION,
  generationCreateBody,
  generationFormSchema,
  generationPatchBody,
  generationPreview,
  generationValues,
  type GenerationFormValues,
  type GenerationPreview,
} from './generationForms';
import { codeOfLabel, composeLabel, labelTail } from './generationLabel';
import type { UseCatalogWriteResult } from './useCatalogWrite';
import {
  createCatalogGeneration,
  patchCatalogGeneration,
} from '../../../api/admin-vehicle-generations';

/**
 * The add / edit form of one generation (AUT-450 / VG-03).
 *
 * - `row` null = add (`POST /models/:id/generations`), else edit
 *   (`PATCH /generations/:id` with ONLY the changed keys + the row's
 *   `updatedAt` verbatim). `row` is read from the cached list on every
 *   render; when it changes (refetch after a stale 409) the form re-seeds
 *   with `keepDirtyValues` — the typed values stay.
 * - Add only: until the label is edited by hand, «Рестайлинг» / «Рынок»
 *   re-compose it (`generationLabel.ts`). Edit never rewrites the label.
 * - Save is off while the list is `syncing`, a write is running, a server
 *   error is shown (cleared by editing the field it is about), the display
 *   would exceed 40, or (edit) nothing changed.
 */
export type GenerationServerError = Extract<
  CatalogWriteError,
  { kind: 'generationConflict' | 'tooLong' | 'yearOrder' }
>;

export interface GenerationFormOptions {
  modelId: string;
  row: AdminVehicleGenerationDto | null;
  syncing: boolean;
  write: UseCatalogWriteResult;
  onDone: () => void;
}

export interface GenerationForm {
  form: UseFormReturn<GenerationFormValues>;
  isAdd: boolean;
  stale: boolean;
  serverError: GenerationServerError | null;
  preview: GenerationPreview | null;
  canSave: boolean;
  setLabel: (value: string) => void;
  setRestyling: (value: number) => void;
  setMarket: (value: string) => void;
  /** Clear the server error — only one of `kind` when given. */
  clearServerError: (kind?: GenerationServerError['kind']) => void;
  save: () => Promise<void>;
}

const RESOLVER = zodResolver(generationFormSchema);
const K = 'vehicleCatalog.generations';

const isServerError = (e: CatalogWriteError): e is GenerationServerError =>
  e.kind === 'generationConflict' || e.kind === 'tooLong' || e.kind === 'yearOrder';

export const useGenerationForm = (o: GenerationFormOptions): GenerationForm => {
  const { t } = useTranslation();
  const [stale, setStale] = useState(false);
  const [serverError, setServerError] = useState<GenerationServerError | null>(null);
  const auto = useRef(o.row === null);
  const form = useForm<GenerationFormValues>({
    resolver: RESOLVER,
    mode: 'onChange',
    defaultValues: o.row ? generationValues(o.row) : EMPTY_GENERATION,
  });

  useEffect(() => {
    if (o.row) form.reset(generationValues(o.row), { keepDirtyValues: true });
    // Re-seed only when the cached row object changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [o.row]);

  const opts = (value: unknown): { shouldDirty: true; shouldValidate: boolean } => ({
    shouldDirty: true,
    shouldValidate: value !== '',
  });

  const retail = (restyling: number, market: string): void => {
    setServerError(null);
    const v = form.getValues();
    if (auto.current) {
      const code = codeOfLabel(v.label, labelTail(t, v.restyling, v.market)) ?? v.label;
      const label = composeLabel(code, labelTail(t, restyling, market));
      form.setValue('label', label, opts(label));
    }
    form.setValue('restyling', restyling, opts(restyling));
    form.setValue('market', market, opts(market));
  };

  const values = form.watch();
  const preview = generationPreview(values);
  const patch = o.row ? generationPatchBody(values, o.row) : null;
  const canSave =
    !o.syncing &&
    !o.write.busy &&
    serverError === null &&
    !(preview?.over ?? false) &&
    (o.row === null || patch !== null);

  const save = async (): Promise<void> => {
    const v = form.getValues();
    const { row } = o;
    const body = row ? generationPatchBody(v, row) : null;
    if ((row && body === null) || o.syncing) return;
    const outcome = await o.write.run<AdminVehicleGenerationDto>(
      () =>
        row && body
          ? patchCatalogGeneration(row.id, body)
          : createCatalogGeneration(o.modelId, generationCreateBody(v)),
      { successKey: row ? `${K}.saved` : `${K}.created` },
    );
    if (outcome.ok) {
      setStale(false);
      o.onDone();
      return;
    }
    if (outcome.error.kind === 'stale') setStale(true);
    if (isServerError(outcome.error)) setServerError(outcome.error);
  };

  return {
    form,
    isAdd: o.row === null,
    stale,
    serverError,
    preview,
    canSave,
    setLabel: (value) => {
      setServerError((e) => (e?.kind === 'tooLong' ? null : e));
      if (
        auto.current &&
        codeOfLabel(value, labelTail(t, values.restyling, values.market)) === null
      )
        auto.current = false;
      form.setValue('label', value, opts(value));
    },
    setRestyling: (value) => retail(value, form.getValues('market')),
    setMarket: (value) => retail(form.getValues('restyling'), value),
    clearServerError: (kind) =>
      setServerError((e) => (kind === undefined || e?.kind === kind ? null : e)),
    save,
  };
};
